import api from './api';

export interface PlatformRec { id: string; fit: string; reason: string; }
export interface Suggestion {
  icp_summary: string;
  keywords: string[];
  platforms: PlatformRec[];
  sources: { reddit: string[]; telegram: string[]; facebook: string[] };
  _fallback?: boolean;
}

export interface SetupStatus {
  onboarding_completed: boolean;
  keyword_count: number;
  connections: { facebook: boolean; telegram: boolean; reddit: boolean; youtube: boolean };
}

export interface TargetLocation { city: string; country: string; }

export interface BusinessProfile {
  website: string | null;
  description: string | null;
  target_locations: TargetLocation[] | null;
  onboarding_completed: boolean;
  last_suggestion: Suggestion | null;
}

export async function getSetupStatus(): Promise<SetupStatus> {
  const { data } = await api.get('/settings/setup-status');
  return data;
}

export async function getProfile(): Promise<BusinessProfile> {
  const { data } = await api.get('/settings/profile');
  return data;
}

export async function updateProfile(
  website: string, description: string, target_locations: TargetLocation[],
): Promise<BusinessProfile> {
  const { data } = await api.put('/settings/profile', { website, description, target_locations });
  return data;
}

// ── Reddit sources (per-company subreddits) ──
export interface RedditSourceRow { id: string; subreddit: string; enabled: boolean; }

export async function listRedditSources(): Promise<RedditSourceRow[]> {
  const { data } = await api.get('/reddit-sources');
  return data.sources || [];
}

export async function addSubreddit(subreddit: string): Promise<void> {
  await api.post('/reddit-sources', { subreddit });
}

export async function deleteRedditSource(id: string): Promise<void> {
  await api.delete(`/reddit-sources/${id}`);
}

// ── YouTube sources (per-company search queries) ──
export interface YoutubeSourceRow { id: string; query: string; enabled: boolean; }

export async function listYoutubeSources(): Promise<YoutubeSourceRow[]> {
  const { data } = await api.get('/youtube-sources');
  return data.sources || [];
}

export async function addYoutubeQuery(query: string): Promise<void> {
  await api.post('/youtube-sources', { query });
}

export async function deleteYoutubeSource(id: string): Promise<void> {
  await api.delete(`/youtube-sources/${id}`);
}

export async function analyzeBusiness(
  website: string, description: string, target_locations: TargetLocation[],
): Promise<Suggestion> {
  const { data } = await api.post('/settings/analyze-business', { website, description, target_locations });
  return data;
}

export async function completeOnboarding(): Promise<void> {
  await api.post('/settings/complete-onboarding');
}

export async function requestAccess(platform: string): Promise<void> {
  await api.post('/settings/request-access', { platform });
}

export async function bulkAddSubreddits(subreddits: string[]): Promise<void> {
  await api.post('/reddit-sources/bulk', { subreddits });
}

// Reuse the keywords endpoint for saving the reviewed list
export async function saveKeywords(keywords: string[]): Promise<string[]> {
  const { data } = await api.put('/settings/keywords', { keywords });
  return data.keywords || [];
}
